import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormFieldComponent } from '../form-fields/shared/form-field/form-field.component';
import { InputComponent } from '../form-fields/input/input.component';
import { BadgeComponent } from '../badge/badge.component';
import { FormModel } from '../../utils/form-model';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { SelectComponent } from '../form-fields/select/select.component';
import { OptionComponent } from '../form-fields/select/option.component';
import { ButtonComponent } from '../button/button.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgxMaskModule } from 'ngx-mask';
import { ButtonContentComponent } from '../button/components/button-content.component';
import { ModalRef } from '../modal/modal-ref';
import { CepResponse, CepService } from 'src/app/services/cep.service';
import { EMPTY, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { LoginService } from 'src/app/services/login.service';
import { PatientService } from 'src/app/services/patient.service';
import {
  ERepresentativeRelationship,
  Patient,
  RepresentativePatient,
} from 'src/app/interfaces/patient.interface';
import { AddressService } from 'src/app/services/address.service';
import { Address } from 'src/app/interfaces/address.interface';
import { SwitchButtonComponent } from '../switch-button/switch-button.component';

type PatientForm = {
  patientCpf: string;
  patientName: string;
  patientBirthdate: string;
  patientMobile: string;
  patientEmail: string;
  hasLegalRepresentative: boolean;
};

type PatientAddressForm = {
  cep: string;
  state: string;
  city: string;
  neighborhood: string;
  address: string;
  number: string;
  complement: string;
};

type PatientRepresentativeForm = {
  representativeName: string;
  representativeCpf: string;
  representativeRelationship: ERepresentativeRelationship;
  representativeMobile: string;
  representativeEmail: string | null;
};

export type PatientFormPrefillData = {
  name?: string;
  identification?: string;
  mobile?: string;
  birthdate?: string;
  email?: string;
  postalcode?: string;
};

type PatientRepresentativeFormGroup = FormModel<PatientRepresentativeForm>;
type PatientAddressFormGroup = FormModel<PatientAddressForm>;
type PatientFormGroup = FormModel<PatientForm> & {
  address: FormGroup<PatientAddressFormGroup>;
  representative: FormGroup<PatientRepresentativeFormGroup>;
};
@Component({
  selector: 'app-patient-form',
  templateUrl: 'patient-form.component.html',
  styleUrls: ['patient-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormFieldComponent,
    ReactiveFormsModule,
    InputComponent,
    BadgeComponent,
    SelectComponent,
    OptionComponent,
    ButtonComponent,
    FontAwesomeModule,
    NgxMaskModule,
    ButtonContentComponent,
    SwitchButtonComponent,
  ],
})
export class PatientFormComponent implements OnInit {
  private readonly destroy$ = new Subject<void>();
  private readonly cepService = inject(CepService);
  private readonly modalRef = inject(ModalRef);
  private readonly loginService = inject(LoginService);
  private readonly patientService = inject(PatientService);
  private readonly addressService = inject(AddressService);

  patientForm = new FormGroup<PatientFormGroup>({
    patientCpf: new FormControl(null, {
      validators: [Validators.required, this.cpfValidator()],
    }),
    patientName: new FormControl(null, {
      validators: [Validators.required, Validators.maxLength(100)],
    }),
    patientBirthdate: new FormControl(null, {
      validators: [Validators.required, this.birthdateValidator()],
    }),
    patientMobile: new FormControl(null, { validators: [Validators.required] }),
    patientEmail: new FormControl(null, {
      validators: [Validators.required, this.emailValidator()],
    }),
    hasLegalRepresentative: new FormControl(false, { validators: [Validators.required] }),

    address: new FormGroup({
      cep: new FormControl(null),
      state: new FormControl({ value: null, disabled: true }),
      city: new FormControl({ value: null, disabled: true }),
      neighborhood: new FormControl({ value: null, disabled: true }, this.requiredTrimmed()),
      address: new FormControl({ value: null, disabled: true }, this.requiredTrimmed()),
      number: new FormControl({ value: null, disabled: true }),
      complement: new FormControl({ value: null, disabled: true }),
    }),

    representative: new FormGroup({
      representativeName: new FormControl(null),
      representativeCpf: new FormControl(null),
      representativeRelationship: new FormControl(null),
      representativeMobile: new FormControl(null),
      representativeEmail: new FormControl(null),
    }),
  });

  patientFormControl = this.patientForm.controls;
  addressFormControl = this.patientFormControl.address.controls;
  representativeFormControl = this.patientFormControl.representative.controls;

  hasRepresentative: boolean = false;
  disableToggleRepresentative: boolean = false;

  hasAddress: boolean = false;
  showErrorCep: boolean = false;
  isFormDisabled: boolean = false;
  isLoadingCep: boolean = false;

  patientId!: number;

  addressId: number | null = null;

  patientRepresentative: RepresentativePatient;

  prefillData?: PatientFormPrefillData;

  ngOnInit(): void {
    this.valueChangesCep();
    this.valueChangesCpf();
    this.valueChangesBirthdate();
    this.valueChangesHasRepresentative();

    if (this.patientId) {
      this.getPatientDetails();
    } else if (this.prefillData) {
      this.applyPrefillData(this.prefillData);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private valueChangesHasRepresentative(): void {
    const patientFormControl = this.patientFormControl;
    patientFormControl.hasLegalRepresentative.valueChanges
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((value) => {
        const controls = this.representativeFormControl;
        this.hasRepresentative = value;

        if (this.hasRepresentative) {
          controls.representativeCpf.setValidators([Validators.required, this.cpfValidator()]);
          controls.representativeMobile.setValidators([Validators.required]);
          controls.representativeName.setValidators([Validators.required]);
          controls.representativeRelationship.setValidators([Validators.required]);
          controls.representativeEmail.setValidators([this.emailValidator()]);

          if (this.patientId && this.patientRepresentative) {
            const representative = this.patientRepresentative;
            controls.representativeCpf.setValue(representative.cpf);
            controls.representativeMobile.setValue(representative.mobile);
            controls.representativeName.setValue(representative.name);
            controls.representativeRelationship.setValue(representative.relationshipToPatient);
            controls.representativeEmail.setValue(representative.email ?? null);
          }
        } else {
          controls.representativeCpf.setValue(null);
          controls.representativeMobile.setValue(null);
          controls.representativeName.setValue(null);
          controls.representativeRelationship.setValue(null);
          controls.representativeEmail.setValue(null);

          controls.representativeCpf.clearValidators();
          controls.representativeMobile.clearValidators();
          controls.representativeName.clearValidators();
          controls.representativeRelationship.clearValidators();
        }

        Object.values(controls).forEach((item) => {
          item.updateValueAndValidity();
          item.markAsPristine();
          item.markAsUntouched();
        });
      });
  }

  public getRelationshipOptions(): ERepresentativeRelationship[] {
    return Object.values(ERepresentativeRelationship);
  }

  public onPatientFormSubmit(): void {
    this.isFormDisabled = true;

    const patientControl = this.patientFormControl;
    const patient: Patient = {
      id: this.patientId ?? null,
      name: patientControl.patientName.value.trim(),
      cpf: patientControl.patientCpf.value,
      birthdate: this.parseBirthdateToISO(patientControl.patientBirthdate.value),
      email: patientControl.patientEmail?.value ?? null,
      mobile: patientControl.patientMobile.value,
      hasLegalRepresentative: this.hasRepresentative,
    };

    let representative: RepresentativePatient;

    if (this.hasRepresentative) {
      const representativeControl = this.representativeFormControl;
      representative = {
        name: representativeControl.representativeName.value,
        cpf: representativeControl.representativeCpf.value,
        mobile: representativeControl.representativeMobile.value,
        relationshipToPatient: representativeControl.representativeRelationship.value,
        email: representativeControl.representativeEmail?.value ?? null,
      };
    }
    let address: Address;

    if (this.hasAddress) {
      const addressControl = this.addressFormControl;

      if (addressControl.address.invalid || addressControl.neighborhood.invalid){
        addressControl.address.markAsTouched();
        addressControl.neighborhood.markAsTouched();
        return;
      }

      address = {
        id: this.addressId ?? null,
        accountId: null,
        patientId: null,
        zipCode: addressControl.cep.value,
        address: addressControl.address.value.trim(),
        addressNumber: addressControl.number.value.trim(),
        complement: addressControl.complement.value?.trim() || null,
        neighborhood: addressControl.neighborhood.value.trim(),
        city: addressControl.city.value,
        state: addressControl.state.value,
      };
    }

    this.patientService
      .savePatient({ ...patient, representative: this.hasRepresentative ? representative : null })
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        switchMap((response: Patient) => {
          if (!this.hasAddress) {
            return of(response);
          }

          return this.addressService
            .saveAddress({ ...address, patientId: response.id, accountId: response.accountId })
            .pipe(map(() => response));
        }),
        finalize(() => (this.isFormDisabled = false))
      )
      .subscribe((patient) => {
        this.loginService.showMessage(`Cliente salvo com sucesso.`);
        this.closeModal(patient);
      });
  }

  public closeModal(data?: Patient): void {
    this.modalRef.close(data ?? null);
  }

  private valueChangesCep(): void {
    this.addressFormControl.cep.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        switchMap((cep) => {
          const addressControl = this.addressFormControl;

          if (cep.length < 8) {
            this.hasAddress = false;
            addressControl.number.setValue(null);
            addressControl.number.disable();
            addressControl.number.clearValidators();
            addressControl.complement.disable();
            addressControl.state.setValue(null);
            addressControl.city.setValue(null);
            addressControl.address.setValue(null);
            addressControl.address.disable();
            addressControl.neighborhood.setValue(null);
            addressControl.neighborhood.disable();
            return EMPTY;
          }

          this.isLoadingCep = true;
          addressControl.number.setValidators([Validators.required]);
          addressControl.number.disable();
          addressControl.cep.disable();
          addressControl.complement.disable();

          return this.cepService.getAddressByZipCode(cep).pipe(
            take(1),
            tap((response: CepResponse) => {
              this.hasAddress = true;

              addressControl.cep.enable();
              addressControl.number.enable();
              addressControl.complement.enable();

              addressControl.state.setValue(response.uf);
              addressControl.city.setValue(response.localidade);

              if(response.logradouro){
                addressControl.address.setValue(response.logradouro);
              } else {
                addressControl.address.enable();
              }

              if(response.bairro){
                addressControl.neighborhood.setValue(response.bairro);
              } else {
                addressControl.neighborhood.enable();
              }
            }),
            finalize(() => (this.isLoadingCep = false)),
            catchError(() => {
              this.showCepError();
              this.hasAddress = false;
              addressControl.cep.enable();
              return EMPTY;
            })
          );
        })
      )
      .subscribe();
  }

  private valueChangesCpf(): void {
    this.patientFormControl.patientCpf.valueChanges
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        filter((cpf) => !!cpf),
        filter(() => this.patientFormControl.patientCpf.valid),
        switchMap((cpf: string) => {
          const patientCpfControl = this.patientFormControl.patientCpf;

          return this.patientService.patientExist(cpf).pipe(
            take(1),
            tap((response: boolean) => {
              if (!!response) {
                patientCpfControl.setErrors({
                  alreadyExist: true,
                });

                return EMPTY;
              }
            })
          );
        })
      )
      .subscribe();
  }

  private valueChangesBirthdate(): void {
    this.patientFormControl.patientBirthdate.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((birthdate: string) => {
        const date = this.parsePatientBirthdate(birthdate);
        if (!date) {
          return;
        }

        const isUnderage = !this.isPatientAdult(date);

        this.disableToggleRepresentative = isUnderage;

        if (isUnderage && !this.hasRepresentative) {
          const patientHasRepresentativeControl = this.patientFormControl.hasLegalRepresentative;
          patientHasRepresentativeControl.setValue(true);
          patientHasRepresentativeControl.updateValueAndValidity();
        }
      });
  }

  private showCepError(): void {
    this.showErrorCep = true;

    setTimeout(() => {
      this.showErrorCep = false;
    }, 2000);
  }

  private parsePatientBirthdate(birthdate: string): Date | null {
    if (!birthdate || birthdate.length !== 8) {
      return null;
    }

    const day = Number(birthdate.slice(0, 2));
    const month = Number(birthdate.slice(2, 4)) - 1;
    const year = Number(birthdate.slice(4, 8));

    const date = new Date(year, month, day);

    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
      return null;
    }

    const currentYear = new Date().getFullYear();

    if (currentYear - 120 >= date.getFullYear()) {
      return null;
    }

    return date;
  }

  private isPatientAdult(date: Date): boolean {
    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();

    const hasNotHadBirthdayThisYear =
      today.getMonth() < date.getMonth() ||
      (today.getMonth() === date.getMonth() && today.getDate() < date.getDate());

    if (hasNotHadBirthdayThisYear) {
      age--;
    }

    return age >= 18;
  }

  private birthdateValidator(): ValidatorFn {
    return (control) => {
      const value = control.value;

      if (!value || value.length !== 8) {
        return { invalid: true };
      }

      const date = this.parsePatientBirthdate(value);

      if (!date) {
        return { invalid: true };
      }

      return null;
    };
  }

  private cpfValidator(): ValidatorFn {
    return (control) => {
      const value = control.value;

      if (!this.loginService.isCpfPatternValid(value)) {
        return { invalid: true };
      }

      return null;
    };
  }

  private emailValidator(): ValidatorFn {
    return (control) => {
      const value = control.value;

      if (!value) {
        return null;
      }

      if (!this.loginService.isEmailPatternValid(value)) {
        return { invalid: true };
      }

      return null;
    };
  }

  private parseBirthdateToISO(birthdate: string): string {
    const day = birthdate.slice(0, 2);
    const month = birthdate.slice(2, 4);
    const year = birthdate.slice(4, 8);

    return `${year}-${month}-${day}`;
  }

  private parseISOToBirthdate(birthdate: string): string {
    if (!birthdate) return null;

    const clean = birthdate.replace(/\D/g, '');

    const year = clean.slice(0, 4);
    const month = clean.slice(4, 6);
    const day = clean.slice(6, 8);

    return `${day}${month}${year}`;
  }

  /**
   * Datas de integrações externas vêm em formatos diferentes (ex.: ADVBOX usa
   * "DD/MM/YYYY", outras podem usar ISO "YYYY-MM-DD") — detecta a ordem pelo
   * tamanho do primeiro segmento em vez de assumir um formato fixo.
   */
  private parseIntegrationBirthdate(birthdate: string): string | null {
    if (!birthdate) return null;

    const parts = birthdate.split(/[/\-.]/).map((part) => part.trim());
    if (parts.length !== 3) return null;

    const isIsoOrder = parts[0].length === 4;
    const year = isIsoOrder ? parts[0] : parts[2];
    const month = parts[1].padStart(2, '0');
    const day = (isIsoOrder ? parts[2] : parts[0]).padStart(2, '0');

    if (year.length !== 4 || month.length !== 2 || day.length !== 2) {
      return null;
    }

    return `${day}${month}${year}`;
  }

  private applyPrefillData(data: PatientFormPrefillData): void {
    const patientControl = this.patientFormControl;

    if (data.name) {
      patientControl.patientName.setValue(data.name);
    }

    if (data.email) {
      patientControl.patientEmail.setValue(data.email);
    }

    if (data.mobile) {
      // mobile é o whatsapp da integração; é o único telefone considerado (ignora "phone").
      patientControl.patientMobile.setValue(data.mobile.replace(/\D/g, ''));
    }

    if (data.birthdate) {
      patientControl.patientBirthdate.setValue(this.parseIntegrationBirthdate(data.birthdate));
    }

    if (data.identification) {
      const digits = data.identification.replace(/\D/g, '');
      if (digits.length === 11) {
        patientControl.patientCpf.setValue(digits);
      }
      // 14 dígitos (CNPJ) ou qualquer outro tamanho: não preenche, o campo exige que o
      // usuário digite o CPF manualmente.
    }

    if (data.postalcode) {
      // Só preenche o CEP; valueChangesCep() já cuida de buscar e popular o restante do endereço.
      this.addressFormControl.cep.setValue(data.postalcode.replace(/\D/g, ''));
    }

    this.patientForm.markAllAsTouched();
  }

  private getPatientDetails(): void {
    this.patientService
      .getPatientById(this.patientId)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        tap((response: Patient) => {
          const patient = response;
          const patientControl = this.patientFormControl;
          this.patientRepresentative = patient.representative ?? null;

          patientControl.patientName.setValue(patient.name);
          patientControl.patientMobile.setValue(patient.mobile);
          patientControl.patientEmail.setValue(patient?.email ?? null);
          patientControl.patientCpf.setValue(patient.cpf, { emitEvent: false });
          patientControl.patientCpf.disable();
          patientControl.patientBirthdate.setValue(this.parseISOToBirthdate(patient.birthdate));

          if (patient.addresses && patient.addresses.length > 0) {
            const address = patient.addresses[0];
            const addressControl = this.addressFormControl;
            this.hasAddress = true;

            this.addressId = address.id;
            addressControl.address.setValue(address.address);
            addressControl.cep.setValue(address.zipCode, { emitEvent: false });
            addressControl.number.setValue(address.addressNumber);
            addressControl.city.setValue(address.city);
            addressControl.neighborhood.setValue(address.neighborhood);
            addressControl.state.setValue(address.state);
            addressControl.complement.setValue(address.complement);

            addressControl.number.enable();
            addressControl.complement.enable();
            addressControl.address.enable();
            addressControl.neighborhood.enable();
          }

          if (patient.hasLegalRepresentative) {
            const representative = patient.representative;
            const representativeControl = this.representativeFormControl;
            patientControl.hasLegalRepresentative.setValue(true);

            representativeControl.representativeName.setValue(representative.name);
            representativeControl.representativeCpf.setValue(representative.cpf);
            representativeControl.representativeRelationship.setValue(
              representative.relationshipToPatient
            );
            representativeControl.representativeMobile.setValue(representative.mobile);
            representativeControl.representativeEmail.setValue(representative?.email ?? null);
          }

          if (this.isPatientAdult(new Date(patient.birthdate))) {
            this.disableToggleRepresentative = true;
          }

          this.patientForm.updateValueAndValidity();
        })
      )
      .subscribe({
        error: (err) => {
          const message =
            err?.error?.message || err?.message || 'Erro ao carregar dados do cliente';
          this.loginService.showMessage(message);
        },
      });
  }

  private requiredTrimmed(): ValidatorFn {
    return (control) => {
      const value = control.value;

      if (!value?.trim()) {
        return { required: true };
      }

      return null;
    };
  }
}
